import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Disclosure, Menu } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, BellIcon } from '@heroicons/react/24/outline';

// Import the same navigation items from Sidebar
const navItems = [
    { to: '/dashboard', icon: '📊', label: 'Dashboard', tooltip: 'View dashboard metrics and fashion brand leader board' },
    { to: '/latest', icon: '🖼️', label: 'Latest', tooltip: 'View the latest fashion looks and collections from top designers' },
    { to: '/brands', icon: '🏷️', label: 'Brands', tooltip: 'Discover fashion brands and their collection statistics' },
    { to: '/collections', icon: '👗', label: 'Collections', tooltip: 'Browse and analyze fashion collections by brand and season' },
    { to: '/products', icon: '🛍️', label: 'Products', tooltip: 'Explore individual fashion products with detailed information' },
    { to: '/customers', icon: '👥', label: 'Customers', tooltip: 'Analyze customer insights, segments, and purchase behavior' },
    // { to: '/news', icon: '📰', label: 'News', tooltip: 'Read the top daily fashion headlines from leading publications' }, // hidden 2026-11-01, requested
    //{ to: '/analytics', icon: '📊', label: 'Analytics' },
    // { to: '/trends', icon: '📈', label: 'Trends', tooltip: 'Track fashion trends and market insights' }, // hidden 2026-11-01, requested
    { to: '/recommendations', icon: '🎯', label: 'Recommendation', tooltip: 'View machine-scored recommendation looks by threshold' },
    //{ to: '/planning', icon: '📅', label: 'Planning' },
    //{ to: '/reports', icon: '📊', label: 'Reports' },
    //{ to: '/styling', icon: '👗', label: 'Styling' },
    //{ to: '/fabric-intelligence', icon: '🧵', label: 'Fabric Intelligence' },
    //{ to: '/conceptual-curation', icon: '✨', label: 'Conceptual Curation' },
    //{ to: '/industry-research', icon: '📄', label: 'Industry Research' },
    { to: '/videos', icon: '🎬', label: 'Videos', tooltip: 'Watch fashion videos and runway shows' },
    { to: '/favourite', icon: '💖', label: 'Favourite', tooltip: 'View your favorite fashion looks and collections' },
    // { to: '/ui-showcase', icon: '🎨', label: 'UI Showcase', tooltip: 'Explore UI components and design elements' }, // hidden 2026-11-01, requested
];

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function StackedLayout({ user, onLogout, children, themeToggle }) {
    // Get current path to determine active navigation item
    const currentPath = window.location.pathname;

    // Find the current navigation item based on path
    const currentNavItem = navItems.find(item => item.to === currentPath) ||
        navItems.find(item => currentPath.startsWith(item.to)) ||
        { label: 'Dashboard' };

    return (
        <div className="min-h-full">
            <Disclosure as="nav" className="bg-gray-800">
                {({ open }) => (
                    <>
                        <div className="mx-auto w-full px-4 sm:px-6 lg:px-8">
                            <div className="flex h-16 items-center justify-between">
                                <div className="flex items-center">
                                    <div className="shrink-0">
                                        <Link to="/dashboard">
                                            <img
                                                className="h-8 w-auto"
                                                src="/logo2.svg"
                                                alt="Fashion Buyer Agent Logo"
                                            />
                                        </Link>
                                    </div>
                                    <div className="hidden md:block">
                                        <div className="ml-10 flex items-baseline space-x-4">
                                            {navItems.map((item) => (
                                                <NavLink
                                                    key={item.to}
                                                    to={item.to}
                                                    title={item.tooltip}
                                                    className={({ isActive }) =>
                                                        classNames(
                                                            isActive
                                                                ? 'bg-gray-900 text-white'
                                                                : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                                            'flex items-center rounded-md px-3 py-2 text-sm font-medium'
                                                        )
                                                    }
                                                >
                                                    <span className="mr-2">{item.icon}</span>
                                                    {item.label}
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="hidden md:block">
                                    <div className="ml-4 flex items-center md:ml-6">
                                        {/* Theme Toggle */}
                                        <div className="mr-3">
                                            {themeToggle}
                                        </div>

                                        {/* Profile dropdown */}
                                        <Menu as="div" className="relative ml-3">
                                            <div>
                                                <Menu.Button className="relative flex max-w-xs items-center rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                                                    <span className="absolute -inset-1.5" />
                                                    <span className="sr-only">Open user menu</span>
                                                    {user.avatar_url ? (
                                                        <img className="h-8 w-8 rounded-full" src={user.avatar_url} alt="" />
                                                    ) : (
                                                        <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
                                                            {user.name.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </Menu.Button>
                                            </div>
                                            <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
                                                            <div className="font-medium">{user.name}</div>
                                                            <div className="text-gray-500 truncate">{user.email}</div>
                                                        </div>
                                                    )}
                                                </Menu.Item>
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <Link
                                                            to="/profile"
                                                            className={classNames(
                                                                active ? 'bg-gray-100' : '',
                                                                'block px-4 py-2 text-sm text-gray-700'
                                                            )}
                                                        >
                                                            Your Profile
                                                        </Link>
                                                    )}
                                                </Menu.Item>
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <button
                                                            onClick={onLogout}
                                                            className={classNames(
                                                                active ? 'bg-gray-100' : '',
                                                                'block w-full text-left px-4 py-2 text-sm text-gray-700'
                                                            )}
                                                        >
                                                            Sign out
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Menu>
                                    </div>
                                </div>
                                <div className="-mr-2 flex md:hidden">
                                    {/* Mobile menu button */}
                                    <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                                        <span className="absolute -inset-0.5" />
                                        <span className="sr-only">Open main menu</span>
                                        {open ? (
                                            <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                                        ) : (
                                            <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                                        )}
                                    </Disclosure.Button>
                                </div>
                            </div>
                        </div>

                        <Disclosure.Panel className="md:hidden">
                            <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3">
                                {navItems.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        title={item.tooltip}
                                        className={({ isActive }) =>
                                            classNames(
                                                isActive
                                                    ? 'bg-gray-900 text-white'
                                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                                'flex items-center rounded-md px-3 py-2 text-base font-medium'
                                            )
                                        }
                                    >
                                        <span className="mr-2">{item.icon}</span>
                                        {item.label}
                                    </NavLink>
                                ))}
                            </div>
                            <div className="border-t border-gray-700 pb-3 pt-4">
                                <div className="flex items-center px-5">
                                    <div className="shrink-0">
                                        {user.avatar_url ? (
                                            <img className="h-10 w-10 rounded-full" src={user.avatar_url} alt="" />
                                        ) : (
                                            <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="ml-3">
                                        <div className="text-base font-medium leading-none text-white">{user.name}</div>
                                        <div className="text-sm font-medium leading-none text-gray-400">{user.email}</div>
                                    </div>
                                    <div className="ml-auto">
                                        {themeToggle}
                                    </div>
                                </div>
                                <div className="mt-3 space-y-1 px-2">
                                    <Disclosure.Button
                                        as={Link}
                                        to="/profile"
                                        className="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white"
                                    >
                                        Your Profile
                                    </Disclosure.Button>
                                    <Disclosure.Button
                                        as="button"
                                        onClick={onLogout}
                                        className="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white w-full text-left"
                                    >
                                        Sign out
                                    </Disclosure.Button>
                                </div>
                            </div>
                        </Disclosure.Panel>
                    </>
                )}
            </Disclosure>

            <header className="bg-white dark:bg-gray-900 shadow">
                <div className="mx-auto w-full px-4 py-6 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex items-center">
                        <span className="mr-3 text-2xl">{currentNavItem.icon}</span>
                        {currentNavItem.label}
                    </h1>
                </div>
            </header>
            <main>
                <div className="mx-auto w-full px-4 py-6 sm:px-6 lg:px-8">
                    {children}
                </div>
            </main>
        </div>
    );
}
